import React from 'react'
import type { FrameOption } from '@renderer/types/configState'
import type { StickerPlacement } from './stickers'
import { orderedSlots } from './cutCategories'

export interface BkStripProps {
  frame: FrameOption | undefined
  /** Photo URLs in slot order; short arrays simply leave later slots empty. */
  photos: (string | undefined)[]
  stickers?: StickerPlacement[]
  className?: string
}

/**
 * A Bingkai Kenangan strip, composited the way it will print.
 *
 * Positions come from the frame's own `drawParams` as percentages of its
 * canvas, so one component serves the 600x2400 four-cut and the 1200x2400
 * eight-cut without knowing which it has — and a re-authored border moves the
 * preview with it, because nothing about the geometry is hard-coded here.
 *
 * Layer order matches the seeded frames and the print: photographs first, then
 * the border art over them, then any stickers the guest placed on top.
 */
const BkStrip: React.FC<BkStripProps> = ({ frame, photos, stickers = [], className = '' }) => {
  if (!frame) return <div className={className} />

  const { canvasWidth: W, canvasHeight: H, images } = frame.drawParams
  const slots = orderedSlots(frame)
  const overlay = images.find((i) => i.type === 'image' && i.src)

  const pct = (n: number, of: number): string => `${(n / of) * 100}%`

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ aspectRatio: `${W} / ${H}` }}>
      {slots.map((slot, i) => (
        <div
          key={`${slot.type}-${i}`}
          className="absolute overflow-hidden bg-black/10"
          style={{
            left: pct(slot.left, W),
            top: pct(slot.top, H),
            width: pct(slot.width, W),
            height: pct(slot.height, H)
          }}
        >
          {photos[i] && <img src={photos[i]} alt="" className="h-full w-full object-cover" />}
        </div>
      ))}

      {overlay?.src && (
        <img
          src={overlay.src}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
      )}

      {stickers.map((s) => (
        <img
          key={s.id}
          src={s.url}
          alt=""
          className="pointer-events-none absolute"
          style={{
            left: `${s.xPct}%`,
            top: `${s.yPct}%`,
            width: `${s.widthPct}%`,
            transform: `translate(-50%, -50%) rotate(${s.rotation}deg)`
          }}
        />
      ))}
    </div>
  )
}

export default BkStrip
